import { useEffect } from 'react';
import {
  useFeatureIsOn,
  useFeatureValue,
  useGrowthBook,
} from '@growthbook/growthbook-react';
import {
  FeatureFlagAdapter,
  featureFlagAdapter,
  FeatureFlagAttributes,
  FeatureFlagKeys,
  Flags,
} from '../shared';
import { FeatureFlagClient } from './feature-flag.config';
import { FeatureFlagInitOptions } from './feature-flag.types';

/**
 * Hook to get the FeatureFlag context
 * @returns {FeatureFlagClient} The Feature Flag instance.
 */
export const useFeatureFlagContext = () => {
  const growthBook = useGrowthBook<Flags>();

  const setAttributes = (attributes: FeatureFlagAttributes) => {
    growthBook.setAttributes(attributes);
  };
  const updateAttributes = (attributes: FeatureFlagAttributes) => {
    growthBook.updateAttributes(attributes);
  };
  const getAttributes = () => {
    return growthBook.getAttributes();
  };

  return {
    setAttributes,
    updateAttributes,
    getAttributes,
  };
};

/**
 * Hook to get the feature flag instance.
 * @property id - The unique identifier for the feature flag instance.
 * @property fallback - The fallback value for the feature flag.
 */
export const useGetFeatureFlag = <T extends keyof Flags>(
  id: T,
  fallback: Flags[T],
  adapter: Omit<FeatureFlagAdapter, 'flag'>,
) =>
  useFeatureValue<Flags[T]>(
    featureFlagAdapter({
      flag: id,
      ...adapter,
    }),
    fallback,
  );

/**
 * Hook for boolean feature flags.
 */
export const useBooleanFeatureFlag = (
  flag: FeatureFlagKeys,
  adapter: Omit<FeatureFlagAdapter, 'flag'>,
) =>
  useFeatureIsOn(
    featureFlagAdapter({
      flag,
      ...adapter,
    }),
  );

/*
 * Initializes the feature flag SDK.
 */
export const useFeatureFlagInit = ({
  initOptions,
  featureFlag,
}: {
  featureFlag: FeatureFlagClient;
  initOptions?: FeatureFlagInitOptions;
}) => {
  useEffect(() => {
    featureFlag
      .init({
        /**
         * When enabled, changes to features  will be streamed to the SDK in realtime as they are published.
         * @property streaming
         */
        streaming: true,
        /**
         * If the network request takes longer than this (in milliseconds), continue
         * Default: `0` (no timeout)
         * @property timeout
         */
        timeout: 5000,
        ...initOptions,
      })
      .then((initResult) => {
        if (!featureFlag.inDevMode()) {
          return;
        }

        if (initResult.success) {
          console.log('[Feature Flag] Initialized successfully');
        } else {
          console.error(
            '[Feature Flag] Initialization failed:',
            initResult.error,
          );
        }
      });
  }, []);
};
